/* ==========================================================================
   Golden Grove — theme.js
   No dependencies, no framework, no build step.

   Public API (other section scripts should use this rather than re-implementing):

     window.GG.reducedMotion()            -> boolean, live
     window.GG.announce(message)          -> polite live-region announcement
     window.GG.dialog(element, options)   -> focus trap + Escape + scroll lock
     window.GG.scrollToTop()              -> honours the smoother
     window.GG.flyToCart(sourceElement)   -> the arc-into-the-cart animation
     window.GG.cart.add(items, options)   -> POST /cart/add.js  (+ section render)
                                             options.open: true / false forces the
                                             drawer; omit it to follow the
                                             "Open the basket when something is
                                             added" theme setting
     window.GG.cart.change(key, qty)      -> POST /cart/change.js
     window.GG.cart.refresh()             -> re-render the drawer from the server
     window.GG.cart.open() / .close()
     window.GG.observeReveals(root)       -> wire up reveals inside injected markup

   Events dispatched on window:
     'intro:done'   — the intro overlay has lifted (the hero video waits for it)
     'cart:updated' — detail: { cart } after any successful cart mutation
     'cart:error'   — detail: { message }

   Events listened for on window:
     'cart:fly'     — detail: { x, y, w, h, src } — manual fly-to-cart trigger

   DATA ATTRIBUTE HOOKS — see _build-notes/shopify-architecture.md for the
   full list. Everything JS touches is a data-* attribute, never a class name,
   so restyling can never break behaviour.
   ========================================================================== */

(function () {
  "use strict";

  /* ------------------------------------------------------------------------
     Utilities
     ------------------------------------------------------------------------ */

  var motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

  function reducedMotion() {
    return motionQuery.matches;
  }

  function $(selector, scope) {
    return (scope || document).querySelector(selector);
  }

  function $$(selector, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(selector));
  }

  function on(target, type, handler, options) {
    if (target) target.addEventListener(type, handler, options);
  }

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  /* Session storage is unavailable in some private-browsing modes. Every read
     and write goes through these so a throw can never take the page down. */
  function sessionGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  }

  function sessionSet(key, value) {
    try {
      window.sessionStorage.setItem(key, value);
    } catch (error) {
      /* ignore */
    }
  }

  /* ------------------------------------------------------------------------
     Live region
     ------------------------------------------------------------------------ */

  /* The region lives in theme.liquid, outside anything the Section Rendering
     API replaces — otherwise replaceWith() destroys the node before a screen
     reader has read it. */
  function announce(message) {
    var region = document.getElementById("gg-live-region");
    if (!region || !message) return;
    region.textContent = "";
    window.requestAnimationFrame(function () {
      region.textContent = message;
    });
  }

  /* ------------------------------------------------------------------------
     Scroll lock — reference counted, because overlays can stack
     ------------------------------------------------------------------------ */

  var lockCount = 0;
  var previousOverflow = null;

  function lockScroll() {
    if (lockCount === 0) {
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
    }
    lockCount += 1;
  }

  function unlockScroll() {
    lockCount = Math.max(0, lockCount - 1);
    if (lockCount === 0) {
      /* Restore the PREVIOUS value, not "" — the intro overlay may have set it. */
      document.documentElement.style.overflow = previousOverflow || "";
      previousOverflow = null;
    }
  }

  /* ------------------------------------------------------------------------
     Dialog helper — focus trap, Escape, scroll lock, focus restore
     ------------------------------------------------------------------------

     Used by the cart drawer and every modal. The focusable set is recomputed
     on each Tab rather than cached on open, so a dialog whose contents are
     replaced mid-flight (the cart drawer, after a section re-render) keeps a
     correct trap.                                                            */

  var FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]),' +
    "select:not([disabled]), textarea:not([disabled]), iframe, object, embed," +
    '[tabindex]:not([tabindex="-1"]), [contenteditable="true"], summary, details';

  function visibleFocusables(root) {
    return $$(FOCUSABLE, root).filter(function (element) {
      if (element.hasAttribute("disabled")) return false;
      if (element.getAttribute("aria-hidden") === "true") return false;
      if (element.closest("[hidden]")) return false;
      /* offsetParent is null for display:none. position:fixed needs the rect check. */
      return element.offsetParent !== null || element.getClientRects().length > 0;
    });
  }

  function dialog(element, options) {
    if (!element) return null;
    var settings = options || {};
    var panel = settings.panel || $("[data-dialog-panel]", element) || element;
    var isOpen = false;
    var lastFocused = null;

    function onKeydown(event) {
      if (!isOpen) return;

      if (event.key === "Escape" || event.key === "Esc") {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== "Tab") return;

      var items = visibleFocusables(panel);
      if (items.length === 0) {
        event.preventDefault();
        panel.focus({ preventScroll: true });
        return;
      }

      var first = items[0];
      var last = items[items.length - 1];
      var active = document.activeElement;

      /* Focus can end up outside the panel without a Tab having moved it — a
         section re-render destroys the focused node and the browser falls back
         to <body>. Pull it back in before the wrap-around checks, or a forward
         Tab escapes an aria-modal dialog entirely. */
      if (!panel.contains(active)) {
        event.preventDefault();
        if (event.shiftKey) last.focus({ preventScroll: true });
        else first.focus({ preventScroll: true });
        return;
      }

      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus({ preventScroll: true });
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    }

    /* A scrollable region with no focusable child cannot be reached by
       keyboard, so arrow keys scroll nothing (WCAG 2.1.1). Mark the scroller
       with data-scroll-region and this gives it a tab stop — but only while it
       actually overflows, so a short dialog gains no pointless stop. */
    function syncScrollRegions() {
      $$("[data-scroll-region]", panel).forEach(function (region) {
        if (region.scrollHeight - region.clientHeight > 1) {
          region.setAttribute("tabindex", "0");
        } else {
          region.removeAttribute("tabindex");
        }
      });
    }

    function open(trigger) {
      if (isOpen) return;
      isOpen = true;
      lastFocused = trigger || document.activeElement;
      element.classList.add("is-open");
      element.removeAttribute("aria-hidden");
      lockScroll();
      document.addEventListener("keydown", onKeydown, true);

      /* preventScroll matters: focusing a control low in a tall scrollable
         dialog otherwise scrolls the panel past its own top. */
      window.requestAnimationFrame(function () {
        syncScrollRegions();
        var target = settings.initialFocus ? $(settings.initialFocus, panel) : null;
        if (!target) target = visibleFocusables(panel)[0] || panel;
        if (!panel.hasAttribute("tabindex")) panel.setAttribute("tabindex", "-1");
        target.focus({ preventScroll: true });
      });

      if (typeof settings.onOpen === "function") settings.onOpen();
    }

    function close() {
      if (!isOpen) return;
      isOpen = false;
      element.classList.remove("is-open");
      element.setAttribute("aria-hidden", "true");
      unlockScroll();
      document.removeEventListener("keydown", onKeydown, true);

      if (lastFocused && typeof lastFocused.focus === "function") {
        lastFocused.focus({ preventScroll: true });
      }
      lastFocused = null;

      if (typeof settings.onClose === "function") settings.onClose();
    }

    /* Backdrop and any [data-dialog-close] inside close it. An anchor carrying
       the attribute must still navigate — preventDefault only belongs on
       controls that have nowhere else to go. */
    on(element, "click", function (event) {
      var closer = event.target.closest("[data-dialog-close]");
      if (closer && element.contains(closer)) {
        if (!closer.hasAttribute("href")) event.preventDefault();
        close();
      }
    });

    return {
      open: open,
      close: close,
      syncScrollRegions: syncScrollRegions,
      toggle: function (trigger) {
        if (isOpen) close();
        else open(trigger);
      },
      isOpen: function () {
        return isOpen;
      },
      element: element,
      panel: panel
    };
  }

  /* Generic wiring: any [data-dialog-open="ID"] button controls #ID. */
  var dialogRegistry = {};

  function getDialog(id) {
    if (dialogRegistry[id]) return dialogRegistry[id];
    var element = document.getElementById(id);
    if (!element) return null;
    dialogRegistry[id] = dialog(element);
    return dialogRegistry[id];
  }

  function initDialogTriggers() {
    on(document, "click", function (event) {
      var trigger = event.target.closest("[data-dialog-open]");
      if (!trigger) return;
      var instance = getDialog(trigger.getAttribute("data-dialog-open"));
      if (!instance) return;
      event.preventDefault();
      instance.open(trigger);
    });
  }

  /* ------------------------------------------------------------------------
     Smooth scroll — a small rAF lerp, standing in for Lenis
     ------------------------------------------------------------------------

     lerp 0.18 is deliberate. Lower values read as the page LAGGING behind the
     wheel rather than as smoothness.

     Touch is left native on purpose. Hand-rolled touch smoothing costs more in
     broken momentum and pull-to-refresh than it buys, and the original's
     touchMultiplier only ever affected feel, not behaviour.                   */

  var LERP = 0.18;
  var WHEEL_MULTIPLIER = 1;

  var smoother = {
    enabled: false,
    target: 0,
    current: 0,
    frame: null,
    maxScroll: 0
  };

  function measureMaxScroll() {
    smoother.maxScroll = Math.max(
      0,
      document.body.scrollHeight - window.innerHeight
    );
  }

  function smootherTick() {
    var delta = smoother.target - smoother.current;

    if (Math.abs(delta) < 0.25) {
      smoother.current = smoother.target;
      window.scrollTo(0, smoother.current);
      smoother.frame = null;
      return;
    }

    smoother.current += delta * LERP;
    window.scrollTo(0, smoother.current);
    smoother.frame = window.requestAnimationFrame(smootherTick);
  }

  function startSmoother() {
    if (smoother.frame === null) {
      smoother.frame = window.requestAnimationFrame(smootherTick);
    }
  }

  function syncSmoother() {
    smoother.current = window.scrollY || window.pageYOffset || 0;
    smoother.target = smoother.current;
  }

  function initSmoothScroll() {
    if (reducedMotion()) return;
    if (document.documentElement.getAttribute("data-smooth-scroll") === "false") return;

    smoother.enabled = true;

    /* The smoother writes scroll positions every frame. Native smooth
       behaviour would fight it, so turn it off while we are driving. */
    document.documentElement.style.scrollBehavior = "auto";

    syncSmoother();
    measureMaxScroll();

    /* Watch document.body, NOT <html>. Pinning <html> means a ResizeObserver
       on it never fires — that bug froze the scroll range on the old site. */
    if (typeof ResizeObserver === "function") {
      var observer = new ResizeObserver(function () {
        measureMaxScroll();
        smoother.target = clamp(smoother.target, 0, smoother.maxScroll);
      });
      observer.observe(document.body);
    }

    on(window, "resize", function () {
      measureMaxScroll();
      smoother.target = clamp(smoother.target, 0, smoother.maxScroll);
    });

    on(
      window,
      "wheel",
      function (event) {
        if (event.ctrlKey) return; /* pinch zoom */
        if (lockCount > 0) return; /* an overlay owns the scroll */
        if (event.target && event.target.closest && event.target.closest("[data-scroll-native]")) {
          return; /* overlays and any subtree that wants native scrolling */
        }

        event.preventDefault();
        measureMaxScroll();

        var delta = event.deltaY;
        if (event.deltaMode === 1) delta *= 16; /* lines  */
        if (event.deltaMode === 2) delta *= window.innerHeight; /* pages */

        smoother.target = clamp(
          smoother.target + delta * WHEEL_MULTIPLIER,
          0,
          smoother.maxScroll
        );
        startSmoother();
      },
      { passive: false }
    );

    /* Anything that scrolls the page without going through us — keyboard,
       scrollbar drag, anchor jump, focus() — resyncs the smoother. */
    on(
      window,
      "scroll",
      function () {
        if (smoother.frame !== null) return;
        syncSmoother();
      },
      { passive: true }
    );
  }

  function scrollToTop() {
    if (smoother.enabled) {
      measureMaxScroll();
      smoother.target = 0;
      startSmoother();
      return;
    }
    window.scrollTo({ top: 0, behavior: reducedMotion() ? "auto" : "smooth" });
  }

  /* Always land at the top on a full page load. */
  function initScrollRestoration() {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }
    if (!window.location.hash) {
      window.scrollTo(0, 0);
    }
  }

  /* ------------------------------------------------------------------------
     Reveal on scroll
     ------------------------------------------------------------------------

     Content is visible by default in CSS and only hidden once <html> carries
     .js. If IntersectionObserver is missing, or an observer never fires, the
     fallback timer reveals everything — content is never permanently blank.  */

  var revealObserver = null;

  function revealElement(element) {
    element.classList.add("is-revealed");
  }

  function animationsEnabled() {
    return document.documentElement.getAttribute("data-reveal-animations") !== "false";
  }

  function observeReveals(root) {
    if (!animationsEnabled()) return;

    var scope = root || document;
    var items = $$("[data-reveal]", scope).filter(function (element) {
      return !element.classList.contains("is-revealed");
    });

    if (items.length === 0) return;

    items.forEach(function (element) {
      var delay = element.getAttribute("data-reveal-delay");
      if (delay) element.style.setProperty("--reveal-delay", delay + "ms");
    });

    if (reducedMotion() || typeof IntersectionObserver !== "function") {
      items.forEach(revealElement);
      return;
    }

    if (!revealObserver) {
      revealObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            revealElement(entry.target);
            revealObserver.unobserve(entry.target);
          });
        },
        { rootMargin: "-80px" }
      );
    }

    items.forEach(function (element) {
      revealObserver.observe(element);
    });

    /* Safety net. Some mobile browsers miss the trigger entirely. */
    window.setTimeout(function () {
      items.forEach(function (element) {
        if (!element.classList.contains("is-revealed")) {
          revealElement(element);
          if (revealObserver) revealObserver.unobserve(element);
        }
      });
    }, 1600);
  }

  /* ------------------------------------------------------------------------
     Title reveal — masked word rise
     ------------------------------------------------------------------------

     Markup contract: put data-title-reveal on the heading. Its text content is
     split into words at run time, so the server HTML stays plain readable text
     and a no-JS reader sees a normal heading.                                */

  var TITLE_STAGGER = 60;
  var TITLE_DURATION = 900;
  var TITLE_FALLBACK = 1400;

  function buildTitleReveal(heading) {
    var text = heading.textContent.replace(/\s+/g, " ").trim();
    if (!text) return null;

    /* Screen readers get the whole string, not word-by-word fragments. */
    if (!heading.hasAttribute("aria-label")) heading.setAttribute("aria-label", text);

    var words = text.split(" ");
    var fragment = document.createDocumentFragment();

    words.forEach(function (word, index) {
      var mask = document.createElement("span");
      mask.className = "title-reveal__mask";
      mask.setAttribute("aria-hidden", "true");

      var inner = document.createElement("span");
      inner.className = "title-reveal__word";
      inner.style.setProperty("--word-delay", index * TITLE_STAGGER + "ms");
      inner.textContent = word;

      mask.appendChild(inner);
      fragment.appendChild(mask);
    });

    heading.textContent = "";
    heading.appendChild(fragment);
    heading.classList.add("title-reveal");

    return words.length;
  }

  function releaseTitle(heading) {
    heading.classList.add("is-revealed");
    /* Release the masks once the run finishes. Holding overflow:hidden clips
       the heading when a reader forces a taller line-height (WCAG 1.4.12). */
    window.setTimeout(function () {
      heading.classList.add("is-released");
    }, TITLE_DURATION + TITLE_STAGGER * 6);
  }

  function initTitleReveals(root) {
    var headings = $$("[data-title-reveal]", root || document).filter(function (heading) {
      return !heading.classList.contains("title-reveal");
    });

    if (headings.length === 0) return;

    if (reducedMotion() || !animationsEnabled()) {
      /* Plain headings — no masks, no spans. */
      return;
    }

    headings.forEach(function (heading) {
      if (!buildTitleReveal(heading)) return;

      var onMount = heading.hasAttribute("data-title-reveal-immediate");

      if (onMount || typeof IntersectionObserver !== "function") {
        window.requestAnimationFrame(function () {
          releaseTitle(heading);
        });
      } else {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (!entry.isIntersecting) return;
              releaseTitle(entry.target);
              observer.unobserve(entry.target);
            });
          },
          { rootMargin: "0px 0px -10% 0px" }
        );
        observer.observe(heading);
      }

      /* Mandatory. On some mobile browsers the in-view trigger misses
         entirely, which would leave a clipped heading permanently blank.
         This happened on a real device. Do not remove. */
      window.setTimeout(function () {
        if (!heading.classList.contains("is-revealed")) releaseTitle(heading);
      }, TITLE_FALLBACK);
    });
  }

  /* ------------------------------------------------------------------------
     Header — state, hide-on-scroll, mobile menu
     ------------------------------------------------------------------------ */

  var SOLID_AT = 30;
  var HIDE_AFTER = 240;
  var HIDE_THRESHOLD = 4;

  function initHeader() {
    var header = $("[data-header]");
    if (!header) return;

    var toggle = $("[data-menu-toggle]", header);
    var menu = $("[data-mobile-menu]", header);
    var allowTransparent = header.getAttribute("data-header-transparent") === "true";

    var lastY = window.scrollY || 0;
    var menuOpen = false;
    var ticking = false;

    function setState() {
      var y = window.scrollY || 0;

      if (allowTransparent && y <= SOLID_AT && !menuOpen) {
        header.classList.add("header--transparent");
        header.classList.remove("header--solid");
      } else {
        header.classList.add("header--solid");
        header.classList.remove("header--transparent");
      }

      if (menuOpen || y <= HIDE_AFTER) {
        header.classList.remove("header--hidden");
      } else if (y - lastY > HIDE_THRESHOLD) {
        header.classList.add("header--hidden");
      } else if (lastY - y > HIDE_THRESHOLD) {
        header.classList.remove("header--hidden");
      }

      lastY = y;
      ticking = false;
    }

    on(
      window,
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(setState);
      },
      { passive: true }
    );

    setState();

    function closeMenu() {
      if (!menuOpen) return;
      menuOpen = false;
      if (menu) menu.classList.remove("mobile-menu--open");
      if (toggle) toggle.setAttribute("aria-expanded", "false");
      header.classList.remove("header--menu-open");
      setState();
    }

    function openMenu() {
      if (menuOpen) return;
      menuOpen = true;
      if (menu) menu.classList.add("mobile-menu--open");
      if (toggle) toggle.setAttribute("aria-expanded", "true");
      header.classList.add("header--menu-open");
      header.classList.remove("header--hidden");
      setState();
    }

    on(toggle, "click", function () {
      if (menuOpen) closeMenu();
      else openMenu();
    });

    on(document, "keydown", function (event) {
      if (event.key === "Escape" && menuOpen) {
        closeMenu();
        if (toggle) toggle.focus();
      }
    });

    on(document, "click", function (event) {
      if (!menuOpen) return;
      if (header.contains(event.target)) {
        /* A nav click inside the menu closes it. */
        if (event.target.closest(".mobile-menu__link")) closeMenu();
        return;
      }
      closeMenu();
    });

    /* The header force-shows whenever the cart changes, so the fly-to-cart
       clone always has a visible place to land and the badge pop is seen. */
    on(window, "cart:updated", function () {
      header.classList.remove("header--hidden");
      var badge = $("[data-cart-count]", header);
      if (!badge) return;
      badge.classList.remove("is-bumped");
      /* Reflow so the animation restarts on a repeat add. */
      void badge.offsetWidth;
      badge.classList.add("is-bumped");
    });
  }

  /* ------------------------------------------------------------------------
     Back to top
     ------------------------------------------------------------------------ */

  function initBackToTop() {
    var button = $("[data-back-to-top]");
    if (!button) return;

    var ticking = false;

    function update() {
      button.classList.toggle("is-visible", (window.scrollY || 0) > 700);
      ticking = false;
    }

    on(
      window,
      "scroll",
      function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();

    on(button, "click", function () {
      scrollToTop();
    });
  }

  /* ------------------------------------------------------------------------
     Magnetic buttons
     ------------------------------------------------------------------------ */

  var MAGNETIC_STRENGTH = 0.3;

  function initMagnetic() {
    if (reducedMotion()) return;
    if (!window.matchMedia("(pointer: fine)").matches) return;

    $$("[data-magnetic]").forEach(function (element) {
      var rect = null;

      on(element, "pointerenter", function () {
        /* Measured once on enter, not per move — that is a layout read per
           pointer event otherwise. */
        rect = element.getBoundingClientRect();
        element.classList.remove("is-returning");
      });

      on(element, "pointermove", function (event) {
        if (!rect) return;
        var x = (event.clientX - (rect.left + rect.width / 2)) * MAGNETIC_STRENGTH;
        var y = (event.clientY - (rect.top + rect.height / 2)) * MAGNETIC_STRENGTH;
        element.style.transform = "translate(" + x + "px, " + y + "px)";
      });

      on(element, "pointerleave", function () {
        rect = null;
        element.classList.add("is-returning");
        element.style.transform = "translate(0, 0)";
      });
    });
  }

  /* ------------------------------------------------------------------------
     Fly to cart
     ------------------------------------------------------------------------ */

  var FLY_DURATION = 700;

  function cartTargetRect() {
    var icon = document.getElementById("cart-icon");
    if (!icon) return null;

    var rect = icon.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;

    var header = icon.closest("[data-header]");

    /* The clone now leaves on the click rather than on the server response, so
       the header has to come back now too — otherwise the jar flies at a target
       that only becomes visible after it has landed. The cart:updated listener
       does the same thing; this just stops it being the first to. */
    if (header) header.classList.remove("header--hidden");

    /* If the header is mid-hide it carries a translate, and the clone would
       fly off-screen. Correct back to the header's resting position. */
    if (header && typeof window.DOMMatrix === "function") {
      var transform = window.getComputedStyle(header).transform;
      if (transform && transform !== "none") {
        try {
          var matrix = new window.DOMMatrix(transform);
          rect = new window.DOMRect(rect.left - matrix.e, rect.top - matrix.f, rect.width, rect.height);
        } catch (error) {
          /* keep the uncorrected rect */
        }
      }
    }

    return rect;
  }

  function flyFromRect(source) {
    if (reducedMotion()) return;
    if (!source || !source.src || !source.w || !source.h) return;

    var target = cartTargetRect();
    if (!target) return;

    var layer = $("[data-fly-layer]");
    if (!layer) return;

    var clone = document.createElement("img");
    clone.className = "fly-clone";
    clone.src = source.src;
    clone.alt = "";
    clone.setAttribute("aria-hidden", "true");
    clone.style.left = source.x + "px";
    clone.style.top = source.y + "px";
    clone.style.width = source.w + "px";
    clone.style.height = source.h + "px";
    layer.appendChild(clone);

    var dx = target.left + target.width / 2 - (source.x + source.w / 2);
    var dy = target.top + target.height / 2 - (source.y + source.h / 2);

    var animation = clone.animate(
      [
        { transform: "translate(0, 0) scale(1)", opacity: 1 },
        { transform: "translate(" + dx * 0.6 + "px, " + dy * 0.35 + "px) scale(0.6)", opacity: 1, offset: 0.6 },
        { transform: "translate(" + dx + "px, " + dy + "px) scale(0.16)", opacity: 0.4 }
      ],
      { duration: FLY_DURATION, easing: "cubic-bezier(0.4, 0, 0.2, 1)", fill: "forwards" }
    );

    animation.onfinish = function () {
      clone.remove();
    };
    /* Belt and braces — if the Web Animations API misbehaves, still clean up. */
    window.setTimeout(function () {
      if (clone.parentNode) clone.remove();
    }, FLY_DURATION + 400);
  }

  function flyToCart(element) {
    if (!element) return;
    var image = element.tagName === "IMG" ? element : $("img", element);
    if (!image) return;
    var rect = image.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    flyFromRect({
      x: rect.left,
      y: rect.top,
      w: rect.width,
      h: rect.height,
      src: image.currentSrc || image.src
    });
  }

  function findFlySource(origin) {
    if (!origin) return null;

    var explicit = origin.getAttribute && origin.getAttribute("data-fly-from");
    if (explicit) {
      var named = $(explicit);
      if (named) return named;
    }

    var scope = origin.closest && origin.closest("[data-fly-scope]");
    if (scope) {
      var scoped = $("[data-fly-src]", scope);
      if (scoped) return scoped;
    }

    return $("[data-fly-hero]") || $("[data-fly-src]");
  }

  /* ------------------------------------------------------------------------
     Cart — AJAX API + bundled section rendering
     ------------------------------------------------------------------------

     This is the money path. Rules it follows:
       - URLs are built from window.Shopify.routes.root so locale prefixes live
       - lines are addressed by their line item KEY, never by position; another
         tab mutating the cart shifts positions but never keys
       - every mutation asks for the drawer and the bubble back in the SAME
         response, so there is no second round trip and no client templating
       - a section that fails to render comes back null; always null-check
       - buttons are disabled while a request is in flight so a double click
         cannot double add                                                     */

  var CART_SECTIONS = ["cart-drawer", "cart-bubble"];

  function cartRoot() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root;
    }
    return "/";
  }

  function sendCartRequest(path, body) {
    var payload = body || {};
    payload.sections = CART_SECTIONS.join(",");
    payload.sections_url = window.location.pathname; /* must start with "/" */

    return window
      .fetch(cartRoot() + "cart/" + path + ".js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        },
        body: JSON.stringify(payload)
      })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok) {
            var message = data.description || data.message || "Cart error";
            throw new Error(message);
          }
          return data;
        });
      });
  }

  /* Shopify's Ajax cart has no server-side locking. Two writes in flight at
     once both read the same cart, and the later reply wins — so a jar vanishes
     with every request still answering 200 and nothing shown to the shopper.
     Disabling the pressed control cannot cover it: the shop grid carries five
     independent quick-add forms, so a second card clicked inside one round trip
     is a second, concurrent write.

     Every mutation is therefore threaded through one promise chain — request
     N+1 leaves only once N has settled. Nothing about the *click* waits: the
     pending state, the fly-to-cart and the count bubble all still fire
     immediately, and only the network calls take turns.

     The chain is advanced with a handler on BOTH outcomes, so a rejected
     request can never wedge the queue, and the chain is kept permanently
     resolved so a failure is never inherited by a later mutation. */
  var cartChain = Promise.resolve();

  function cartRequest(path, body) {
    function send() {
      return sendCartRequest(path, body);
    }

    var result = cartChain.then(send, send);
    cartChain = result.then(noop, noop);
    return result;
  }

  function noop() {}

  function applySections(sections) {
    if (!sections) return;

    Object.keys(sections).forEach(function (id) {
      var html = sections[id];
      if (html === null || html === undefined) return; /* section failed to render */

      var parsed = new DOMParser().parseFromString(html, "text/html");
      var incoming =
        parsed.querySelector('[data-section-id="' + id + '"]') ||
        parsed.getElementById("shopify-section-" + id);
      var current =
        document.querySelector('[data-section-id="' + id + '"]') ||
        document.getElementById("shopify-section-" + id);

      if (incoming && current) current.replaceWith(incoming);
    });

    /* Anything newly injected still needs its reveals wiring up. */
    observeReveals(document);
  }

  /* Whether an add should pop the drawer open. Theme settings → Cart and
     shipping → "Open the basket when something is added" is published on
     <html> by theme.liquid, the same way smooth scroll and reveals are. A
     caller may override it either way: [data-open-cart] on a form or add
     button forces it on for that control. */
  function opensOnAdd(override) {
    if (override === true) return true;
    if (override === false) return false;
    return document.documentElement.getAttribute("data-cart-open-on-add") === "true";
  }

  var cartDialog = null;

  function getCartDialog() {
    if (cartDialog) return cartDialog;
    var element = document.getElementById("CartDrawer");
    if (!element) return null;
    cartDialog = dialog(element, { initialFocus: "[data-drawer-initial-focus]" });
    return cartDialog;
  }

  function openCart(trigger) {
    var instance = getCartDialog();
    if (instance) instance.open(trigger);
  }

  function closeCart() {
    var instance = getCartDialog();
    if (instance) instance.close();
  }

  /* Announcement strings are authored in locales/en.default.json and handed to
     JS as data attributes on the live region, with a {count} placeholder — so
     they stay translatable and the plural form stays the language's problem,
     not ours. Nothing is announced on page load; the drawer arrives already
     rendered, so there is no hydration sync to suppress. */
  function cartMessage(kind, count) {
    var region = document.getElementById("gg-live-region");
    if (!region) return "";

    var suffix = count === 1 ? "-one" : "-other";
    var template = region.getAttribute("data-message-" + kind + suffix);
    if (!template) return "";

    return template.replace("{count}", count);
  }

  /* Re-rendering the drawer destroys the button the shopper just pressed, and
     document.activeElement falls back to <body> — outside an aria-modal
     dialog. Every drawer control carries a data-focus-key so the equivalent
     control in the fresh markup can be found again. sections/main-cart.liquid
     does the same thing for the cart page. */
  function restoreFocusKey(key) {
    if (!key) return;

    var target = document.querySelector('[data-focus-key="' + key + '"]');
    if (target && typeof target.focus === "function") {
      target.focus({ preventScroll: true });
      return;
    }

    /* The line was removed. Land inside the drawer rather than on <body>. */
    var drawer = document.getElementById("CartDrawer");
    if (!drawer || !drawer.classList.contains("is-open")) return;

    var fallback =
      drawer.querySelector("[data-drawer-initial-focus]") ||
      drawer.querySelector("[data-dialog-panel]");
    if (fallback && typeof fallback.focus === "function") {
      fallback.focus({ preventScroll: true });
    }
  }

  function afterMutation(data, options) {
    var settings = options || {};

    applySections(data.sections);

    if (settings.focusKey) restoreFocusKey(settings.focusKey);

    var instance = cartDialog;
    if (instance && instance.isOpen()) instance.syncScrollRegions();

    window.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: data } }));

    if (settings.announce !== false) {
      var count = typeof data.item_count === "number" ? data.item_count : 0;
      announce(settings.message || cartMessage(settings.kind || "added", count));
    }

    if (settings.open) openCart(settings.trigger);

    return data;
  }

  function cartError(error) {
    var message = (error && error.message) || "Something went wrong. Please try again.";
    announce(message);
    window.dispatchEvent(new CustomEvent("cart:error", { detail: { message: message } }));
    return message;
  }

  function addToCart(items, options) {
    var list = Array.isArray(items) ? items : [items];
    var settings = options || {};

    var normalised = list
      .map(function (item) {
        var id = Number(item.id);
        var quantity = Number(item.quantity || 1);
        if (!id || quantity < 1) return null;
        var line = { id: id, quantity: quantity };
        if (item.properties) line.properties = item.properties;
        if (item.selling_plan) line.selling_plan = item.selling_plan;
        return line;
      })
      .filter(Boolean);

    if (normalised.length === 0) {
      return Promise.reject(new Error("Nothing to add"));
    }

    /* The badge moves on the click, not on the response — including while this
       add is still queued behind an earlier one. applySections swaps in the
       server's own bubble markup a moment later, which reconciles the guess;
       a rejected add takes its own quantity back off whatever is on screen by
       then, since a later add may have painted over it in the meantime. */
    var added = 0;
    normalised.forEach(function (line) {
      added += line.quantity;
    });
    var predictedCount = readBubbleCount() + added;
    paintBubbleCount(predictedCount);

    return cartRequest("add", { items: normalised }).then(
      function (data) {
        var counted = countFromSections(data.sections);
        data.item_count = counted === null ? predictedCount : counted;

        return afterMutation(data, {
          open: opensOnAdd(settings.open),
          trigger: settings.trigger,
          announce: settings.announce,
          message: settings.message,
          kind: settings.kind
        });
      },
      function (error) {
        paintBubbleCount(readBubbleCount() - added);
        throw error;
      }
    );
  }

  /* /cart/add.js answers with the added lines only — no item_count, verified
     against the live Ajax API. The cart-bubble section came back in that same
     response and already carries the authoritative number, so parse it out of
     there rather than spending a second round trip on GET /cart.js. */
  function countFromSections(sections) {
    if (!sections) return null;

    var html = sections["cart-bubble"];
    if (!html) return null;

    var badge = new DOMParser().parseFromString(html, "text/html").querySelector("[data-cart-count]");
    if (!badge) return 0; /* the badge is only emitted above zero */

    var count = parseInt(badge.textContent, 10);
    return isNaN(count) ? null : count;
  }

  function readBubbleCount() {
    var badge = document.querySelector("[data-cart-count]");
    if (!badge) return 0;

    var count = parseInt(badge.textContent, 10);
    return isNaN(count) ? 0 : count;
  }

  /* Writes only the visible, aria-hidden number. The accessible name beside it
     is left to the server render — a predicted count is not worth speaking,
     and the live region announces the real one when the response lands. */
  function paintBubbleCount(count) {
    var wrapper = document.querySelector('[data-section-id="cart-bubble"]');
    if (!wrapper) return;

    var badge = wrapper.querySelector("[data-cart-count]");

    if (count < 1) {
      if (badge) badge.remove();
      return;
    }

    if (!badge) {
      badge = document.createElement("span");
      badge.className = "header__cart-count";
      badge.setAttribute("data-cart-count", "");
      badge.setAttribute("aria-hidden", "true");
      wrapper.appendChild(badge);
    }

    badge.textContent = count;
  }

  function changeLine(key, quantity, options) {
    var settings = options || {};
    return cartRequest("change", { id: key, quantity: Number(quantity) }).then(function (data) {
      return afterMutation(data, {
        open: settings.open,
        announce: settings.announce,
        message: settings.message,
        focusKey: settings.focusKey,
        kind: "updated"
      });
    });
  }

  function refreshCart() {
    return window
      .fetch(
        window.location.pathname + "?sections=" + encodeURIComponent(CART_SECTIONS.join(",")),
        { headers: { Accept: "application/json" } }
      )
      .then(function (response) {
        return response.json();
      })
      .then(function (sections) {
        applySections(sections);
        return sections;
      });
  }

  /* The cart page's quantity controls are anchors, so they work without
     JavaScript — and `disabled` means nothing on an anchor. Anything that is
     not a form control gets aria-disabled plus pointer-events instead, so a
     second click during a request really is swallowed. */
  function setBusy(element, busy) {
    if (!element) return;

    var isFormControl = "disabled" in element;

    if (busy) {
      element.setAttribute("aria-busy", "true");
      if (isFormControl) {
        element.disabled = true;
      } else {
        element.setAttribute("aria-disabled", "true");
        element.style.pointerEvents = "none";
      }
    } else {
      element.removeAttribute("aria-busy");
      if (isFormControl) {
        element.disabled = false;
      } else {
        element.removeAttribute("aria-disabled");
        element.style.pointerEvents = "";
      }
    }
  }

  function showFormError(form, message) {
    if (!form) return;
    var target = $("[data-form-error]", form);
    if (!target) return;
    target.textContent = message;
    target.hidden = false;
  }

  function clearFormError(form) {
    if (!form) return;
    var target = $("[data-form-error]", form);
    if (!target) return;
    target.textContent = "";
    target.hidden = true;
  }

  /* Where a cart failure should be shown: the drawer, or the cart page. */
  function cartErrorRegion(control) {
    if (!control || !control.closest) return null;
    return control.closest("[data-cart-page], [data-section-id]");
  }

  function initCart() {
    /* Open / close triggers. */
    on(document, "click", function (event) {
      var opener = event.target.closest("[data-cart-open]");
      if (opener) {
        event.preventDefault();
        openCart(opener);
        return;
      }

      /* The empty-basket CTA and every line-item title are anchors carrying
         data-cart-close: they close the drawer AND navigate. Only a control
         with no href gets its default suppressed. */
      var closer = event.target.closest("[data-cart-close]");
      if (closer) {
        if (!closer.hasAttribute("href")) event.preventDefault();
        closeCart();
      }
    });

    /* Quantity and remove, delegated so a re-rendered drawer keeps working.

       cartError() only speaks to the live region, so a sighted shopper who
       hits an inventory ceiling would otherwise watch the number refuse to
       move with no explanation. The message is also written into the
       [data-form-error] paragraph in the drawer footer or the cart summary. */
    on(document, "click", function (event) {
      var control = event.target.closest("[data-cart-change]");
      if (!control) return;

      event.preventDefault();
      if (control.getAttribute("aria-busy") === "true") return;

      var key = control.getAttribute("data-cart-key");
      var quantity = control.getAttribute("data-cart-quantity");
      if (!key || quantity === null) return;

      var region = cartErrorRegion(control);
      clearFormError(region);

      setBusy(control, true);
      changeLine(key, quantity, { focusKey: control.getAttribute("data-focus-key") })
        .catch(function (error) {
          showFormError(region, cartError(error));
        })
        .then(function () {
          setBusy(control, false);
        });
    });

    /* Typed quantity inputs inside the drawer or the cart page. */
    on(document, "change", function (event) {
      var input = event.target.closest("[data-cart-quantity-input]");
      if (!input) return;

      var key = input.getAttribute("data-cart-key");
      if (!key) return;

      var quantity = clamp(parseInt(input.value, 10) || 0, 0, 99);
      input.value = quantity;

      var region = cartErrorRegion(input);
      clearFormError(region);

      setBusy(input, true);
      changeLine(key, quantity, { focusKey: input.getAttribute("data-focus-key") })
        .catch(function (error) {
          showFormError(region, cartError(error));
          input.value = input.defaultValue;
        })
        .then(function () {
          setBusy(input, false);
        });
    });

    /* Product forms. Progressive enhancement: with no fetch, or if anything
       throws before preventDefault, the native POST to /cart/add still works. */
    on(document, "submit", function (event) {
      var form = event.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      if (!window.fetch) return;
      if (form.hasAttribute("data-no-ajax")) return;

      event.preventDefault();
      clearFormError(form);

      var button = form.querySelector('[name="add"], [type="submit"]');
      var idInput = form.querySelector('[name="id"]');
      var quantityInput = form.querySelector('[name="quantity"]');

      if (!idInput || !idInput.value) return;

      setBusy(button, true);

      /* Fly on the click, not on the response. The jar is already on screen and
         the header is already there, so nothing about the arc needs the server;
         waiting for it was the whole of the perceived lag. flyToCart is a no-op
         under prefers-reduced-motion. */
      flyToCart(findFlySource(button || form));

      /* No data-open-cart means "follow the theme setting", not "never open" —
         so the override is left undefined rather than set to false. */
      addToCart(
        { id: idInput.value, quantity: quantityInput ? quantityInput.value : 1 },
        { open: form.hasAttribute("data-open-cart") || undefined }
      )
        .catch(function (error) {
          showFormError(form, cartError(error));
        })
        .then(function () {
          setBusy(button, false);
        });
    });

    /* Buttons that add without a form (recipe modal, quick add). */
    on(document, "click", function (event) {
      var button = event.target.closest("[data-add-to-cart]");
      if (!button) return;
      if (button.closest('form[action*="/cart/add"]')) return; /* the form handles it */

      event.preventDefault();

      var variantId = button.getAttribute("data-variant-id");
      if (!variantId) return;

      setBusy(button, true);
      flyToCart(findFlySource(button));

      addToCart(
        { id: variantId, quantity: button.getAttribute("data-quantity") || 1 },
        { open: button.hasAttribute("data-open-cart") || undefined }
      )
        .catch(function (error) {
          cartError(error);
        })
        .then(function () {
          setBusy(button, false);
        });
    });

    /* Manual fly trigger, for parity with the original cart:fly event. */
    on(window, "cart:fly", function (event) {
      if (event.detail) flyFromRect(event.detail);
    });
  }

  /* ------------------------------------------------------------------------
     Intro overlay
     ------------------------------------------------------------------------

     Runs once per browser session, home page only, and never blocks a deep
     link. Skippable by click, Enter/Space, Escape, the first wheel, or the
     first touchmove.

     Contract for the hero section: read document.documentElement.dataset.intro
     — "running" means wait for the window 'intro:done' event before playing
     the video, "done" means start immediately. Do not rely on the event alone;
     when the intro is skipped it fires before a deferred section script can
     subscribe.                                                                */

  var INTRO_KEY = "hasVisited";

  function initIntro() {
    /* `.intro` has to be part of the selector: this function also puts
       data-intro on <html>, and a bare [data-intro] would match the document
       element first on any later call. */
    var overlay = $(".intro[data-intro]");
    var root = document.documentElement;

    function markDone() {
      root.setAttribute("data-intro", "done");
      sessionSet(INTRO_KEY, "true");
      window.dispatchEvent(new Event("intro:done"));
    }

    if (!overlay) {
      root.setAttribute("data-intro", "done");
      sessionSet(INTRO_KEY, "true");
      return;
    }

    var visited = sessionGet(INTRO_KEY);

    if (visited || reducedMotion()) {
      overlay.hidden = true;
      overlay.remove();
      markDone();
      return;
    }

    root.setAttribute("data-intro", "running");

    /* The overlay covers the page but is not a dialog helper client, so
       nothing stops Tab reaching the header and page behind it. Marking every
       other top-level node inert keeps focus on the one thing that is
       visible, and hides the hidden page from assistive technology too. */
    var inerted = [];

    function setPageInert(on) {
      if (on) {
        Array.prototype.forEach.call(document.body.children, function (node) {
          if (node === overlay) return;
          if (node.id === "gg-live-region") return; /* must stay announceable */
          if (node.hasAttribute("inert")) return;
          node.setAttribute("inert", "");
          inerted.push(node);
        });
        return;
      }

      inerted.forEach(function (node) {
        node.removeAttribute("inert");
      });
      inerted = [];
    }

    var jars = $$("[data-intro-jar]", overlay);
    var perJar = parseInt(overlay.getAttribute("data-intro-per-jar"), 10) || 700;
    var logoDelay = parseInt(overlay.getAttribute("data-intro-logo-delay"), 10) || 950;
    var timers = [];
    var done = false;

    overlay.hidden = false;
    lockScroll();
    setPageInert(true);

    window.requestAnimationFrame(function () {
      if (done) return; /* skipped inside the first frame — do not re-enter */
      overlay.classList.add("is-entered");
    });

    if (jars.length > 0) jars[0].classList.add("is-active");

    /* Only jars 2..n get timers — the first is visible on mount. */
    for (var index = 1; index < jars.length; index += 1) {
      (function (i) {
        timers.push(
          window.setTimeout(function () {
            jars.forEach(function (jar) {
              jar.classList.remove("is-active");
            });
            jars[i].classList.add("is-active");
          }, logoDelay + i * perJar)
        );
      })(index);
    }

    timers.push(
      window.setTimeout(finish, logoDelay + Math.max(jars.length, 1) * perJar)
    );

    function finish() {
      if (done) return;
      done = true;

      timers.forEach(window.clearTimeout);

      /* Released with the class change, not after the fade — the page becomes
         operable as soon as it is being revealed. */
      setPageInert(false);

      /* One coherent exit: the backdrop and the content lift together. The
         entered state is dropped in the same frame so the two cannot fight
         over the content's opacity. */
      overlay.classList.remove("is-entered");
      overlay.classList.add("intro--exiting");

      /* Deliberately shorter than the fades — the page becomes interactive
         before the overlay has fully cleared. */
      window.setTimeout(function () {
        unlockScroll();
        overlay.hidden = true; /* display:none first, so a failed remove still hides it */
        overlay.remove();
        markDone();
      }, 650);
    }

    on(overlay, "click", function () {
      finish();
    });

    on(overlay, "keydown", function (event) {
      if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
        event.preventDefault();
        finish();
      }
    });

    on(window, "keydown", function (event) {
      if (event.key === "Escape" || event.key === "Esc") finish();
    });

    on(window, "wheel", function () {
      finish();
    }, { passive: true, once: true });

    on(window, "touchmove", function () {
      finish();
    }, { passive: true, once: true });

    /* Focus the overlay so Escape / Space reach it without a click first. */
    window.requestAnimationFrame(function () {
      if (done) return; /* already lifting — leave focus on the page */
      overlay.focus({ preventScroll: true });
    });
  }

  /* ------------------------------------------------------------------------
     Boot
     ------------------------------------------------------------------------ */

  function boot() {
    /* FIRST, deliberately. The hero h1 is held at opacity 0 by CSS until this
       builds its word masks, so every millisecond spent here is a millisecond the
       headline is invisible — while its own eyebrow and body copy animate in on
       pure CSS at 200ms and 700ms. Running it eighth meant the headline arrived
       after the paragraph below it. It depends on nothing else, so it goes first. */
    initTitleReveals(document);

    initScrollRestoration();
    initIntro();
    initSmoothScroll();
    initHeader();
    initDialogTriggers();
    initCart();
    observeReveals(document);
    initBackToTop();
    initMagnetic();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  /* The theme editor re-renders sections in place. Re-wire whatever landed. */
  document.addEventListener("shopify:section:load", function (event) {
    observeReveals(event.target);
    initTitleReveals(event.target);
  });

  /* ------------------------------------------------------------------------
     Public API
     ------------------------------------------------------------------------ */

  window.GG = {
    reducedMotion: reducedMotion,
    announce: announce,
    dialog: dialog,
    getDialog: getDialog,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    scrollToTop: scrollToTop,
    flyToCart: flyToCart,
    observeReveals: observeReveals,
    initTitleReveals: initTitleReveals,
    cart: {
      add: addToCart,
      change: changeLine,
      refresh: refreshCart,
      open: openCart,
      close: closeCart,
      root: cartRoot,
      sections: CART_SECTIONS
    }
  };
})();
